/**
 * MultiSearchableSelect — multi-select dropdown with an inline search input.
 * Built on Popover + Command. Trigger shows the selected label when one item
 * is picked, otherwise a "<placeholder> · N" count.
 */

import { useState, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";

export interface MultiSearchableSelectOption {
  value: string;
  label: string;
  title?: string;
  /** When set, the option renders as a coloured soft badge (e.g. task labels). */
  color?: BadgeColor;
}

interface MultiSearchableSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  options: MultiSearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  contentClassName?: string;
  /** Open the popover on mount (e.g. a just-added filter chip). */
  defaultOpen?: boolean;
  /**
   * Optional actions rendered in a footer below the option list (e.g. "New
   * label" / "Manage labels"). Receives a `close` helper so the action can
   * dismiss the popover before navigating or opening a dialog.
   */
  footer?: (helpers: { close: () => void }) => ReactNode;
}

export function MultiSearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No matches.",
  className,
  contentClassName,
  defaultOpen = false,
  footer,
}: MultiSearchableSelectProps) {
  const [open, setOpen] = useState(defaultOpen);

  const valueSet = new Set(value);
  const selectedLabels = value.map(
    (v) => options.find((o) => o.value === v)?.label ?? v,
  );
  // Show the actual selection (joined names, truncated by the trigger). The
  // count alone ("placeholder · 4") read as "no labels" — surface the names.
  const triggerLabel =
    value.length === 0 ? placeholder : selectedLabels.join(", ");
  // When any selected option carries a colour, render the trigger as a row of
  // coloured badges instead of joined text (e.g. task labels).
  const anyColor = value.some((v) =>
    Boolean(options.find((o) => o.value === v)?.color),
  );

  const toggle = (val: string) => {
    if (valueSet.has(val)) {
      onValueChange(value.filter((v) => v !== val));
    } else {
      onValueChange([...value, val]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          title={value.length > 1 ? selectedLabels.join(", ") : undefined}
          className={cn(
            "inline-flex h-8 items-center justify-between gap-2 px-3 text-xs",
            "rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-ring",
            "transition-colors hover:bg-muted/30",
            className,
          )}
        >
          {value.length === 0 ? (
            <span className="truncate text-left text-muted-foreground">
              {placeholder}
            </span>
          ) : anyColor ? (
            <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
              {value.map((v) => {
                const opt = options.find((o) => o.value === v);
                const label = opt?.label ?? v;
                return opt?.color ? (
                  <Badge
                    key={v}
                    color={opt.color}
                    variant="soft"
                    size="sm"
                    className="shrink-0"
                  >
                    {label}
                  </Badge>
                ) : (
                  <span key={v} className="shrink-0 truncate">
                    {label}
                  </span>
                );
              })}
            </span>
          ) : (
            <span className="truncate text-left">{triggerLabel}</span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("p-0", contentClassName ?? "w-[280px]")}
        align="start"
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-9 text-xs"
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const selected = valueSet.has(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => toggle(opt.value)}
                    className="text-xs"
                    title={opt.title ?? opt.label}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3.5 w-3.5",
                        selected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {opt.color ? (
                      <Badge color={opt.color} variant="soft" size="sm">
                        {opt.label}
                      </Badge>
                    ) : (
                      <span className="truncate">{opt.label}</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {value.length > 0 && (
            <div className="border-t p-1.5">
              <button
                type="button"
                onClick={() => onValueChange([])}
                className="w-full rounded py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
              >
                Clear selection
              </button>
            </div>
          )}
          {footer && (
            <div className="border-t p-1">
              {footer({ close: () => setOpen(false) })}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
